import { Autocomplete, Grid, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import { useState } from 'react';
import { gridSize } from '@/shared/constants';

export type CustomAutocompleteArrString = {
  name: string;
  loadingText?: string;
  label: string;
  disabled?: boolean;
  options: string[] | number[];
  optionLabelForEdit?: string;
  isLoadingData: boolean;
  onChangeValue?: (value: any) => void;
  error: FieldError | undefined;
  helperText: React.ReactNode | undefined;
  required?: boolean;
  textFieldKey?: string;
  defaultValue?: string | number;
  control: Control<any, any>;
  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  disableClearable?: boolean;
  endAdornmentInput?: React.ReactNode;
};

function CustomAutocompleteArrString({
  name,
  options,
  isLoadingData,
  error,
  textFieldKey,
  defaultValue,
  control,
  loadingText = 'Cargando...',
  helperText,
  required = true,
  label,
  onChangeValue,
  size = gridSize,
  disabled = false,
  disableClearable = false,
  endAdornmentInput,
}: Readonly<CustomAutocompleteArrString>) {
  return (
    <Grid item {...size}>
      <Controller
        name={name}
        control={control}
        key={textFieldKey ?? defaultValue ?? name}
        defaultValue={defaultValue ?? ''}
        render={({ field }) => {
          const [inputValue, setInputValue] = useState(field.value ?? '');

          const onChange = (_event: any, data: any) => {
            field.onChange(data);
            onChangeValue && onChangeValue(data);
          };

          //============
          // Las dos funciones siguientes permiten seleccionar un valor de las 
          // options basado en el valor del input ingresado, si no esta en las opciones
          // se limpia el valor del formulario y el texto mostrado
          //============
          const handleInputChange = (_e: any, newInputValue: string) => {
            setInputValue(newInputValue);
            if(options.includes(newInputValue as never)){
              onChange(_e, newInputValue);
            }
          };

          const handleBlur = (_e: any) => {
            const isValid = options.includes(inputValue as never);
            if (!isValid) {
              // Limpia tanto el valor del formulario como el texto mostrado
              field.onChange('');
              setInputValue('');
              onChangeValue && onChangeValue('');
            }
            field.onBlur();
          };

          return (
            <Autocomplete
              {...field}
              freeSolo
              loading={isLoadingData}
              loadingText={loadingText}
              options={options}
              getOptionLabel={(option: any) => option}
              disabled={disabled}
              disableClearable={disableClearable}
              value={field.value || ''} // asegura consistencia
              inputValue={inputValue}
              onChange={onChange}
              onInputChange={handleInputChange}
              onBlur={handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    ...(endAdornmentInput && {
                      endAdornment: endAdornmentInput,
                    }),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d3d3d3",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#9395ec", 
                      },
                    },
                  }}
                  disabled={disabled}
                  label={label}
                  variant="outlined"
                  error={!!error}
                  helperText={helperText}
                  required={required}
                />
              )}
            />
          );
        }}
      />
    </Grid>
  );
}

export default CustomAutocompleteArrString;
